import { CartItem } from "../typings/interfaces/pos/cart-item";
import { ICartSession } from "../typings/interfaces/pos/cart-session";
import { DiscountMode, IDiscountResponse } from "../typings";
import {
  DiscountProcessingType,
  ITEM_PROCESSING_TYPES,
  ItemProcessingType,
} from "../typings/interfaces/responses/discountResponse";
import { ALTERATION_SERVICE_ID } from "../hooks/useAlterationPricing";

type DiscountScope = Pick<IDiscountResponse, "serviceIds" | "sessionId" | "processingType">;

const sum = (values: number[]) => values.reduce((acc, v) => acc + v, 0);

// The item types a discount's processingType covers ("REGULAR,PRESS_ONLY",
// "ALL", a single type, or missing = REGULAR).
export const coveredItemTypes = (
  processingType: DiscountProcessingType | null | undefined
): ItemProcessingType[] => {
  const parts = (processingType ?? "REGULAR").split(",").map((p) => p.trim().toUpperCase());
  if (parts.includes("ALL")) return [...ITEM_PROCESSING_TYPES];
  const covered = ITEM_PROCESSING_TYPES.filter((type) => parts.includes(type));
  return covered.length ? covered : ["REGULAR"];
};

// The part of an item's charge a discount covers. Only the base garment price
// is discounted, when the item's service is covered - upcharges and spots are
// always charged in full. Alteration lines count only when the discount
// covers Alteration, so a DC-only discount on a DC shirt with an alteration
// leaves the alteration at full price. Mirrors getItemTotal: Redo is free,
// Alteration Only zeroes just the base price.
// Press Only / Clean Only items are their own service, not a discount: they
// get only a discount whose "Applies To" includes Press Only / Clean Only.
// Custom discounts (ALL) cover every item.
export const getEligibleItemAmount = (
  item: CartItem,
  serviceIds: number[],
  processingType: DiscountProcessingType | null = "ALL"
) => {
  if (item.redoDetails) return 0;
  const itemType: ItemProcessingType = item.pressOnly
    ? "PRESS_ONLY"
    : item.cleanOnly
      ? "CLEAN_ONLY"
      : "REGULAR";
  if (!coveredItemTypes(processingType).includes(itemType)) return 0;
  const garmentCovered = serviceIds.includes(item.serviceType?.id ?? 0);
  const alterationsCovered = serviceIds.includes(ALTERATION_SERVICE_ID);

  const base = item.alterationOnly ? 0 : item.unitPrice * item.qty;
  const alterations = sum(item.alterationItems.map((alt) => alt?.unitPrice ?? 0));

  return (garmentCovered ? base : 0) + (alterationsCovered ? alterations : 0);
};

// What a discount covers in one session (bill). A "This bill only" discount
// carries its sessionId and covers nothing anywhere else.
export const getEligibleSessionAmount = (
  session: ICartSession,
  discount: DiscountScope
) => {
  if (discount.sessionId && discount.sessionId !== session.sessionId) return 0;
  return sum(
    session.cartItems.map((item) =>
      getEligibleItemAmount(item, discount.serviceIds, discount.processingType ?? "REGULAR")
    )
  );
};

// Percentage of the covered amount, or a fixed amount capped at it.
export const calculateDiscountAmount = (
  eligibleAmount: number,
  mode: DiscountMode,
  discountAmount: number
) => {
  if (eligibleAmount <= 0) return 0;
  if (mode === DiscountMode.PERCENTAGE) {
    return Number(((eligibleAmount * discountAmount) / 100).toFixed(2));
  }
  return Math.min(discountAmount, eligibleAmount);
};

// A checkout (pickup) discount on one bill, worked out on the original
// prices before any earlier discount. A predefined discount passes its
// serviceIds and covers only those lines, the same per-line rule as
// check-in. A custom checkout discount has no service choice (null) and
// covers the whole subtotal.
export const calculateCheckoutDiscount = (
  items: CartItem[],
  subtotal: number,
  serviceIds: number[] | null,
  mode: DiscountMode,
  discountAmount: number,
  processingType: DiscountProcessingType | null = "REGULAR"
) => {
  const eligible = serviceIds
    ? Math.min(
        sum(items.map((item) => getEligibleItemAmount(item, serviceIds, processingType ?? "REGULAR"))),
        subtotal
      )
    : subtotal;
  return calculateDiscountAmount(eligible, mode, discountAmount);
};

const isPredefined = (discount: IDiscountResponse | null | undefined) =>
  !!discount && /^\d+$/.test(String(discount.id));

const liveDiscount = (
  discount: IDiscountResponse,
  storeDiscounts: IDiscountResponse[]
) => {
  const live = storeDiscounts.find((d) => String(d.id) === String(discount.id));
  return live && live.enabled && !live.deleted ? live : null;
};

// The discount to keep on a bill that is saved with $0 discount: the one the
// cashier picked, else the customer's own - unless the cashier removed the
// discount or used a custom one. Saved as dormant, so if the invoice is later
// edited (e.g. Press Only removed) it can apply again.
export const dormantDiscountFor = ({
  appliedDiscount,
  selectedDiscountId,
  customerDiscountId,
  storeDiscounts,
  discountRemoved,
  customDiscount,
}: {
  appliedDiscount: IDiscountResponse | null;
  selectedDiscountId: string | null;
  customerDiscountId: string | number | null | undefined;
  storeDiscounts: IDiscountResponse[];
  discountRemoved: boolean;
  customDiscount: IDiscountResponse | null;
}): IDiscountResponse | null => {
  if (discountRemoved || customDiscount) return null;
  const chosenId =
    (isPredefined(appliedDiscount) && appliedDiscount!.id) ||
    (selectedDiscountId && selectedDiscountId !== "none" ? selectedDiscountId : null) ||
    (customerDiscountId && customerDiscountId !== "none" ? String(customerDiscountId) : null);
  if (!chosenId) return null;
  const discount = storeDiscounts.find((d) => String(d.id) === String(chosenId));
  if (!discount || !discount.enabled || discount.deleted) return null;
  return { ...discount, dormant: true };
};

// The discount to apply while editing an invoice. A discount the invoice
// was created with applies as saved. A dormant one ($0 when saved) applies
// only while it meets every rule: it still exists and is enabled, the cart
// reaches its minimum bill, and - in the per-line calculation - it covers
// the items' services and processing type.
export const resolveLockedDiscount = (
  locked: IDiscountResponse | null,
  storeDiscounts: IDiscountResponse[],
  cartTotal: number
): IDiscountResponse | null => {
  if (!locked?.dormant) return locked;
  const live = liveDiscount(locked, storeDiscounts);
  if (!live || live.minimumBillValue > cartTotal) return null;
  return live;
};

// How editing an invoice treats its discount. Once anything has been paid
// the discount is locked to what was saved. While nothing is paid, editing
// works like check-in: the saved discount is preselected - a predefined one
// by id (its rules, e.g. minimum bill, apply as usual), a custom or old
// fixed one as the custom discount - and the cashier can change it.
export type EditDiscountState =
  | { locked: true; discount: IDiscountResponse | null; reason: string }
  | { locked: false; selectedId: string | null; custom: IDiscountResponse | null };

const PAID_LOCK_REASON = "This invoice has a payment, so its discount can't be changed.";

// lockReason comes from the BE (null = changeable). An uncleared Pay Later /
// Monthly charge doesn't lock the discount, so the BE decides; an older BE
// without it falls back to "anything paid locks it".
export const editDiscountState = (
  paidAmount: number | null | undefined,
  saved: IDiscountResponse | null,
  lockReason?: string | null
): EditDiscountState => {
  if (lockReason !== undefined) {
    if (lockReason !== null) return { locked: true, discount: saved, reason: lockReason };
  } else if (paidAmount == null || paidAmount > 0) {
    // Unknown (a BE without paidAmount) counts as paid: stay locked.
    return { locked: true, discount: saved, reason: PAID_LOCK_REASON };
  }
  if (!saved) return { locked: false, selectedId: null, custom: null };
  if (isPredefined(saved)) return { locked: false, selectedId: String(saved.id), custom: null };
  const { dormant, ...custom } = saved;
  return { locked: false, selectedId: null, custom };
};

// The discount saved on a bill (session.discount in billString), as the
// discount to lock while that bill is edited. Bills saved before the full
// details were stored keep their original amount as a fixed discount.
export const discountFromSavedBill = (
  saved: { id: number | null; amount: number; details?: IDiscountResponse | null } | undefined,
  allServiceIds: number[]
): IDiscountResponse | null => {
  if (!saved) return null;
  if (!(saved.amount > 0)) return saved.details?.dormant ? saved.details : null;
  if (saved.details) return saved.details;
  const now = new Date().toISOString();
  return {
    id: saved.id != null ? String(saved.id) : "custom-discount",
    discountName: "Discount",
    discountAmount: saved.amount,
    discountMode: DiscountMode.FIXED,
    deleted: false,
    description: "Discount applied when this invoice was created",
    createdTime: now,
    lastModifiedTime: now,
    merchantId: 0,
    storeId: 0,
    serviceIds: [...allServiceIds, ALTERATION_SERVICE_ID],
    processingType: "ALL",
    minimumBillValue: 0,
    enabled: true,
    autoApply: false,
    applyToAnyUser: true,
  };
};
